import { FormikProps } from "formik";
import { AccordionBody, AccordionHeader, AccordionItem } from "../../Accordion";
import CurrencyField from "../../form/CurrencyField";
import IsRequired from "../../form/IsRequired";
import { FormRow, FormRowInput, FormRowLabel } from "../../layouts/FormRow";
import { KlientkontoerklaeringTexts } from "../texts";
import { KlientkontoerklaeringFormValues } from "../../../interfaces/klientkontoerklaering";
import { IsCompleteHelpers } from "../util/isComplete";
import BigNumber from "bignumber.js";
import { FormTextGroup } from "../../form/FormTextGroup";
import { FormRadio } from "../../form/FormRadio";
import ValidationError from "../../form/ValidationError";
import ValidatedField from "../../form/ValidatedField";
import { FormCheck } from "../../form/FormCheck";
import { isOneChecked } from "../../../util/common";

const AESection2 = ({
  formikProps,
  isCompleteHelpers,
  texts,
  locked,
}: {
  texts: KlientkontoerklaeringTexts;
  formikProps: FormikProps<KlientkontoerklaeringFormValues>;
  isCompleteHelpers: IsCompleteHelpers;
  locked?: boolean;
}) => {
  const { touched, errors, values } = formikProps;
  const { advokatens_section_1_complete, set_section_1_touched } =
    isCompleteHelpers;

  const diffenceIsZero =
    (values.advokatens_section_2_1_samletKlienttilsvar_beloeb ?? 0) -
      (values.advokatens_section_2_2_indestaaende_beloeb ?? 0) ===
    0;
  const differenceNumber = new BigNumber(
    values.advokatens_section_2_1_samletKlienttilsvar_beloeb ?? 0,
  )
    .minus(values.advokatens_section_2_2_indestaaende_beloeb ?? 0)
    .toNumber();
  return (
    <AccordionItem
      value="Sektion2"
      className={!advokatens_section_1_complete ? "disabled" : ""}
    >
      <AccordionHeader onClick={set_section_1_touched}>
        <span>2.</span> <span>{texts.advokatens_section_2.title}</span>
      </AccordionHeader>
      <AccordionBody>
        <div className="vstack gap-5">
          {!advokatens_section_1_complete && (
            <div className="callout callout-danger" role="alert">
              Du skal udfylde de foregående sektioner inden du kan gå videre.
            </div>
          )}
          <fieldset disabled={!advokatens_section_1_complete || locked}>
            <legend className="h5 mb-4">
              {texts.advokatens_section_2.subtitle1}
            </legend>
            <FormRow padding="4">
              <FormRowLabel
                section="2.1"
                htmlFor={
                  texts.advokatens_section_2
                    .advokatens_section_2_1_samletKlienttilsvar_input.inputId
                }
                column="col-9"
              >
                {
                  texts.advokatens_section_2
                    .advokatens_section_2_1_samletKlienttilsvar_input.title
                }
                <IsRequired />
              </FormRowLabel>
              <FormRowInput column="col-3">
                <CurrencyField
                  name={
                    texts.advokatens_section_2
                      .advokatens_section_2_1_samletKlienttilsvar_input.inputId
                  }
                  id={
                    texts.advokatens_section_2
                      .advokatens_section_2_1_samletKlienttilsvar_input.inputId
                  }
                  amountName={
                    texts.advokatens_section_2
                      .advokatens_section_2_1_samletKlienttilsvar_input.beloebId
                  }
                  touched={
                    touched.advokatens_section_2_1_samletKlienttilsvar_input
                  }
                  error={
                    errors.advokatens_section_2_1_samletKlienttilsvar_input
                  }
                  wrapper
                />
              </FormRowInput>
            </FormRow>
            <FormRow padding="4">
              <FormRowLabel
                section="2.2"
                htmlFor={
                  texts.advokatens_section_2
                    .advokatens_section_2_2_indestaaende_input.inputId
                }
                column="col-9"
              >
                {
                  texts.advokatens_section_2
                    .advokatens_section_2_2_indestaaende_input.title
                }
                <IsRequired />
              </FormRowLabel>
              <FormRowInput column="col-3">
                <CurrencyField
                  name={
                    texts.advokatens_section_2
                      .advokatens_section_2_2_indestaaende_input.inputId
                  }
                  id={
                    texts.advokatens_section_2
                      .advokatens_section_2_2_indestaaende_input.inputId
                  }
                  amountName={
                    texts.advokatens_section_2
                      .advokatens_section_2_2_indestaaende_input.beloebId
                  }
                  touched={touched.advokatens_section_2_2_indestaaende_input}
                  error={errors.advokatens_section_2_2_indestaaende_input}
                  wrapper
                />
              </FormRowInput>
            </FormRow>
            <FormRow padding="4">
              <FormRowLabel section="2.3" htmlFor="differencen" column="col-9">
                {
                  texts.advokatens_section_2.advokatens_section_2_3_differencen
                    .title
                }
              </FormRowLabel>
              <FormRowInput column="col-3">
                <FormTextGroup
                  formik={formikProps}
                  id="differencen"
                  value={differenceNumber.toLocaleString("da-DK", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                    useGrouping: false,
                  })}
                  disabled
                />
              </FormRowInput>
            </FormRow>
            <hr />
            <fieldset>
              <legend className="h5 my-4">
                {texts.advokatens_section_2.subtitle2}
              </legend>
              {diffenceIsZero ? (
                <FormRow padding="4" className="position-relative">
                  <FormCheck
                    id={
                      texts.advokatens_section_2
                        .advokatens_section_2_3_ingen_regulering.id
                    }
                    isInvalid={isOneChecked(
                      texts.advokatens_section_2
                        .advokatens_section_2_3_ingen_regulering.id,
                      touched,
                      values,
                    )}
                  >
                    Der er ikke noget at regulere.
                  </FormCheck>
                  <ValidationError
                    error={errors["advokatens_section_2_3_ingen_regulering"]}
                    show={touched["advokatens_section_2_3_ingen_regulering"]}
                  />
                </FormRow>
              ) : (
                <>
                  <FormRow padding="4">
                    <div className={`col-9 d-flex align-items-center ps-4`}>
                      <div
                        className={`d-flex align-items-center gap-2 position-relative${
                          errors.advokatens_section_2_3_forMeget_forLidt
                            ? " form-control w-auto is-invalid p-1 pe-6"
                            : ""
                        }`}
                      >
                        <FormRadio
                          name={
                            texts.advokatens_section_2
                              .advokatens_section_2_3_forMeget_forLidt.id
                          }
                          value="forMeget"
                          invalid={
                            errors.advokatens_section_2_3_forMeget_forLidt
                              ? true
                              : false
                          }
                        >
                          For meget indestående
                        </FormRadio>
                        <span className="text-muted opacity-50">/</span>
                        <FormRadio
                          name={
                            texts.advokatens_section_2
                              .advokatens_section_2_3_forMeget_forLidt.id
                          }
                          value="forLidt"
                          invalid={
                            errors.advokatens_section_2_3_forMeget_forLidt
                              ? true
                              : false
                          }
                        >
                          For lidt indestående
                        </FormRadio>
                        <ValidationError
                          error={errors.advokatens_section_2_3_forMeget_forLidt}
                          show={true}
                        />
                      </div>
                    </div>

                    <div className="col-3">
                      <CurrencyField
                        name={
                          texts.advokatens_section_2
                            .advokatens_section_2_3_forMegetellerLidtIndestaaende_input
                            .inputId
                        }
                        id={
                          texts.advokatens_section_2
                            .advokatens_section_2_3_forMegetellerLidtIndestaaende_input
                            .inputId
                        }
                        amountName={
                          texts.advokatens_section_2
                            .advokatens_section_2_3_forMegetellerLidtIndestaaende_input
                            .beloebId
                        }
                        touched={
                          touched.advokatens_section_2_3_forMegetellerLidtIndestaaende_input
                        }
                        error={
                          errors.advokatens_section_2_3_forMegetellerLidtIndestaaende_input
                        }
                      />
                    </div>
                  </FormRow>
                  <FormRow padding="4">
                    <div className="col-9 d-flex gap-2 align-items-center ps-4 position-relative">
                      Regulering
                      <div
                        className={`d-flex gap-2 align-items-center${
                          errors.advokatens_section_2_3_haevet_indsat
                            ? " form-control w-auto is-invalid p-1 pe-5"
                            : ""
                        }`}
                      >
                        <FormRadio
                          name={
                            texts.advokatens_section_2
                              .advokatens_section_2_3_haevet_indsat.id
                          }
                          value="haevet"
                          invalid={
                            errors.advokatens_section_2_3_haevet_indsat
                              ? true
                              : false
                          }
                        >
                          hævet
                        </FormRadio>
                        <span className="text-muted opacity-50">/</span>
                        <FormRadio
                          name={
                            texts.advokatens_section_2
                              .advokatens_section_2_3_haevet_indsat.id
                          }
                          value="indsat"
                          invalid={
                            errors.advokatens_section_2_3_haevet_indsat
                              ? true
                              : false
                          }
                        >
                          indsat
                        </FormRadio>
                        <ValidationError
                          error={errors.advokatens_section_2_3_haevet_indsat}
                          show={true}
                        />
                      </div>
                      den
                    </div>
                    <div className="col-3">
                      <ValidatedField
                        wrapper
                        type="date"
                        id={
                          texts.advokatens_section_2
                            .advokatens_section_2_3_haevet_indsat_dato.id
                        }
                        name={
                          texts.advokatens_section_2
                            .advokatens_section_2_3_haevet_indsat_dato.id
                        }
                        touched={true}
                        error={errors.advokatens_section_2_3_haevet_indsat_dato}
                      />
                    </div>
                  </FormRow>
                </>
              )}
            </fieldset>
          </fieldset>
        </div>
      </AccordionBody>
    </AccordionItem>
  );
};

export default AESection2;
